import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  InputLabel,
  MenuItem,
  Typography,
  Divider,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  useTheme
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select/index";
import {
  DataFieldType,
  CreateDataFieldTemplateBody,
  CreateDataFieldOptionTemplateBody,
  UpdateDataFieldTemplateBody,
  UpdateDataFieldOptionTemplateBody,
} from "../../../../shared/types/DataField";
import HoveredIcon from "../HoveredIconButton";
import StepperDots from "../../StepperDots/StepperDots";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";
import {
  useUpdateDataFieldOptionsTemplateMutation,
  useDeleteDataFieldOptionTemplateMutation,
  useCreateDataFieldOptionTemplateMutation,
  useUpdateDataFieldTemplateMutation,
} from "../../../services/dataFieldsSlice";

export const prettierValue = (val: string) => {
  if (val === "") return "";
  if (val === "multiSelect") {
    return "Multi-Select";
  }

  if (val === 'tileMultiSelect') return 'Tile Multi-Select';
  if (val === 'tileSelect') return 'Tile Select';
  if (val === 'yesNo') return 'Yes/No';
  if (val === 'emailLookup') return 'Email Lookup';
  if (val === 'apmLookup') return 'APM Lookup';
  return val[0].toUpperCase() + val.slice(1);
};

export const getOriginValue = (val: string) => {
  if (val === '') return '';
  if (val === 'Multi-Select') return 'multiSelect';
  if (val === 'Tile Multi-Select') return 'tileMultiSelect';
  if (val === 'Tile Select') return 'tileSelect';
  if (val === 'Yes/No') return 'yesNo';
  if (val === 'Email Lookup') return 'emailLookup';
  if (val === 'APM Lookup') return 'apmLookup';
  return val[0].toLowerCase() + val.slice(1);
};

const toolTipSelection = getOriginValue("Tile Select")
// const tooltipMultiselectFields = ["Request Area", "IAM Type", "Type", "Business Unit", "Regulations", "New datafield for demo at 3PM CST", "Workflow data field"]

const textFieldSx = {
  color: "#222222",
  width:  "100%",
  "& > p": {
    margin: "5px 0px",
    color: "#646669",
    fontSize: typographyTokens.fontSizes.xs,
  },
};
const tooltipTextFieldSx = {
  width: "26%",
  margin: "0 90px 0 0"
  
}

interface FirstStepProps {
  value: CreateDataFieldTemplateBody;
  onChange: (value: { name: string; desc: string }) => void;
}

function FirstStep({ onChange, value }: FirstStepProps) {
  const theme = useTheme();
  return (
    <Box>
      <Stack
        display="flex"
        direction="column"
        alignItems="left"
        spacing={4}
        sx={{ p: 4 }}
      >
        <Box>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222" }} htmlFor="task-name">
            Data Field Name
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="name"
            name="name"
            fullWidth
            value={value.name}
            placeholder="Ex. Does this request involve Nuclear?"
            helperText="This will be the question or statement needing to be answered"
            onChange={(e) => {
              onChange({ name: e.target.value, desc: value.description || "" });
            }}
          />
        </Box>
        <Box>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main :"#222222" }} htmlFor="task-assignment-name">
            Data Field Description
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="description"
            name="description"
            fullWidth
            value={value.description}
            multiline
            rows={4}
            placeholder="Describe the reason for this data field here…"
            onChange={(e) => {
              onChange({ name: value.name, desc: e.target.value });
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

interface SecondStepProps {
  options: CreateDataFieldOptionTemplateBody[];
  type: string;
  onChange: (
    options: CreateDataFieldOptionTemplateBody[],
    type: DataFieldType
  ) => void;
  dataField?: any;
  selectedCheckboxes: number[];
  handleCheckboxChange: (optionId: number) => void;
  onSelectedValueChange: (index: number, value: string) => void;
  onReviewStatementChange: (index: number, value: string) => void;
  onUpdateReviewStatements: (index: number, value: string) => void;
  handleColorChange: (index: number, value: string) => void;
  handleTooltipValueChange: (index: number, value: string)=> void;
  handleSepProgressBarChange: (index: number, value: boolean) => void;
  handleHidePowerBiLinkChange: (index: number, value: string) => void;
}

function SecondStep({
  onChange,
  options,
  type,
  dataField,
  selectedCheckboxes,
  handleCheckboxChange,
  onSelectedValueChange,
  onReviewStatementChange,
  onUpdateReviewStatements,
  handleColorChange,
  handleTooltipValueChange,
  handleSepProgressBarChange,
  handleHidePowerBiLinkChange
}: SecondStepProps) {

  const isTileSelect = type === "tileSelect" || type === "tileMultiSelect";
  const hasOption = type === "select" || type === "multiSelect" || isTileSelect;
  interface colorOption {
    id: number;
    label: string;
    color?: string;
  }

  const theme = useTheme();
  
  const colorOptions: colorOption[] = [
    { id: 1, label: "Orange", color: theme.palette.orange.main },
    { id: 2, label: "Blue", color: theme.palette.blue.main },
    { id: 3, label: "Green", color: theme.palette.darkGreen.main },
    { id: 4, label: "Yellow", color: theme.palette.yellow.darkYellow },
    { id: 5, label: "Red", color: theme.palette.deepRed.main },
  ];
  const [selectedValues, setSelectedValues] = useState<string[]>(Array(options.length).fill(''));
  const [textFieldValues, setTextFieldValues] = useState<string[]>(Array(options.length).fill(''));


  const handleTextFieldChange = (event: SelectChangeEvent<string>, index: number) => {
    const newTextFieldValues = [...textFieldValues];
    newTextFieldValues[index] = event.target.value;
    setTextFieldValues(newTextFieldValues);
    onReviewStatementChange(index, event.target.value); 
  };

  const workflow = dataField?.name === "Workflow data field"

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ width: "338px", mb: "30px" }}>
        <InputLabel id="demo-simple-select-label">Data Field Type</InputLabel>
        <Select
          id="data-field-select"
          value={prettierValue(type)}
          onChange={(e: SelectChangeEvent) => {
            onChange(options, getOriginValue(e.target.value) as DataFieldType);
          }}
          size="small"
          displayEmpty
          input={<OutlinedInput />}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return (
                <Typography sx={{ color: "#7E8083" }}>
                  Choose a data field type from the dropdown
                </Typography>
              );
            }

            return selected;
          }}
        >
          <MenuItem disabled value="">
            <Typography sx={{ color: "#7E8083" }}>
              Choose a data field type from the dropdown
            </Typography>
          </MenuItem>
          {Object.keys(DataFieldType).map((key, index) => {
            const indexOfKey = Object.keys(DataFieldType).indexOf(key);
            const value = Object.values(DataFieldType)[indexOfKey];
            return (
              <MenuItem key={index} value={key}>
                {prettierValue(value)}
              </MenuItem>
            );
          })}
        </Select>
      </Box>
      {hasOption && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: workflow ? "16px" : "10px", mb: "10px" }}>
            <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", width: workflow ? "180px" : "216px", flexShrink: 0, fontWeight: typographyTokens.fontWeights.semibold }}>
              Data Field Options
            </Box>
            
            {dataField?.name === "Key Topics" && hasOption && !isTileSelect && (
              <Grid container spacing={12}>
                <Grid item xs={4} marginLeft={"10px"}>
                  <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", width: "216px" }}>
                     Priority Topics
                  </Box>
                </Grid>
                <Grid item xs={4} style={{ paddingLeft: "54px" }}>
                  <Grid container direction="row">
                    Review Statements
                  </Grid>
                </Grid>
                <Grid item xs={2} style={{ paddingLeft: "43px" }}>
                  <Grid container direction="row">
                    Colors
                  </Grid>
                </Grid>
              </Grid>
            )}

            {isTileSelect && (
              <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", width: workflow ? "180px" : "216px", flexShrink: 0, fontWeight: typographyTokens.fontWeights.semibold }}>
                Data Field Option Icon
              </Box>
            )}
            { isTileSelect && !workflow &&
            <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", flex: 1, paddingLeft: "15px", fontWeight: typographyTokens.fontWeights.semibold }}>
              Tooltip selection
            </Box>
            }
            { isTileSelect && workflow &&
            <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", flex: 1, fontWeight: typographyTokens.fontWeights.semibold }}>
              Description
            </Box>
            }
            { isTileSelect && workflow &&
               <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", width: '120px', textAlign: 'center', flexShrink: 0, fontWeight: typographyTokens.fontWeights.semibold }}>
               SEP status bar
             </Box>
            }
            <Box sx={{ width: workflow ? "40px" : "25px", flexShrink: 0 }}></Box>
          </Box>
          {options.map((option, i) => (
            <Box
              key={i}
              sx={{ 
                display: "flex", 
                alignItems: "flex-start", 
                gap: workflow ? "16px" : "10px",
                mb: workflow ? "20px" : "5px",
                pb: workflow ? "10px" : "0",
                borderBottom: workflow ? "1px solid #e0e0e0" : "none"
              }}
            >
              <Box sx={{ width: workflow ? "180px" : "216px", flexShrink: 0 }}>
                <TextField
                  sx={{textFieldSx, width: '100%' }}
                  size="small"
                  id="datafieldOption"
                  name="datafieldOption"
                  fullWidth
                  rows={4}
                  value={option.value}
                  placeholder="Type an option here."
                  onChange={(e) => {
                    onChange(
                      options.map((item, index) => {
                        if (index === i)
                          return { ...item, value: e.target.value };
                        return item;
                      }),
                      type as DataFieldType
                    );
                  }}
                />
              </Box>
              {dataField?.name === "Key Topics" && hasOption && !isTileSelect && (
                <Grid container spacing={12}>
                  <Grid item xs={4} marginLeft={"15px"}>
                    <FormControlLabel
                      sx={{ fontSize: typographyTokens.fontSizes.body }}
                      control={
                        <Checkbox
                          checked={selectedCheckboxes.includes(option?.value)}
                          onChange={() => handleCheckboxChange(option?.value)}
                        />
                      }
                      label={option?.value}
                    />
                  </Grid>
                  <Grid item xs={4} style={{ paddingLeft: "48px" }}>
                    <TextField
                    sx={textFieldSx}
                    size="small"
                    id="name"
                    name="name"
                    value={option?.reviewStatements}
                    // onChange={(e)=> handleTextFieldChange(e as SelectChangeEvent<string>, i)}
                    onChange={(e)=> onUpdateReviewStatements(i, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3} style={{ paddingLeft: "40px" }}>
                    <Grid container direction="row">
                      <FormControl fullWidth>
                        <Select
                          labelId={`select-label-${i}`}
                          id={`select-${i}`}
                          style={{ height: "40px" }}
                          onChange={(e) => handleColorChange(i, e.target.value)}
                          value={option.colors || ''}
                        >
                          {colorOptions.map((val) => (
                            <MenuItem key={val.id} value={val.color}>
                              {val.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}
      
              {isTileSelect && (
                <Box sx={{ width: workflow ? "180px" : "216px", flexShrink: 0 }}>
                  <TextField
                    sx={{...textFieldSx, width: '100%'}}
                    size="small"
                    id="datafieldOption"
                    name="datafieldOption"
                    fullWidth
                    rows={4}
                    value={option.icon || ""}
                    placeholder="Paste a Font Awesome icon name."
                    onChange={(e) => {
                      onChange(
                        options.map((item, index) => {
                          if (index === i)
                            return { ...item, icon: e.target.value };
                          return item;
                        }),
                        type as DataFieldType
                      );
                    }}
                  />
                </Box>
              )}
               { isTileSelect && !workflow && (
                <Box sx={{ flex: 1 }}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    id="name"
                    name="name"
                    value={option?.multiselectTooltipValue}
                    onChange={(e)=> handleTooltipValueChange(i, e.target.value)}
                  />
                </Box>
              )}
              { isTileSelect && workflow && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RichTextEditor
                    value={option?.multiselectTooltipValue || ""}
                    setValue={(value: string) => handleTooltipValueChange(i, value)}
                    style={{ height: '150px', width: '100%' }}
                    onChangeEvent={true}
                  />
                </Box>
              )}
              { isTileSelect && workflow &&(
                <Box sx={{ width: '120px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexShrink: 0, pt: '10px' }}>
                  <Checkbox
                    checked={!!option?.sepProgressBar}
                    onChange={(e) => handleSepProgressBarChange(i, e.target.checked)}
                  />
                </Box>
               )}
              <Box sx={{ width: workflow ? "40px" : "25px", display: 'flex', justifyContent: 'center', flexShrink: 0, pt: workflow ? '10px' : '7px' }}>
                <HoveredIcon
                  icon="fa-regular fa-trash"
                  hoverIcon="fa-solid fa-trash"
                  onClick={() => {
                    onChange(
                      options.filter((item, index) => index !== i),
                      type as DataFieldType
                    );
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {hasOption && (
          <Button
            sx={{ textTransform: "none", mt: "5px" }}
            variant="text"
            onClick={() =>
              onChange(
                [
                  ...options,
                  { value: "", description: "", dataFieldID: 0, order: 0 },
                ],
                type as DataFieldType
              )
            }
          >
            <i className="fa-solid fa-plus"></i> &nbsp; Add option
          </Button>
      )}
    </Box>
  );
}

export default function AddDataField({
  isClose,
  onClose,
  onAdd,
  dataField,
  toggleEdit,
}: {
  isClose: boolean;
  onClose?: () => void;
  onAdd?: (
    df: CreateDataFieldTemplateBody,
    op: CreateDataFieldOptionTemplateBody[]
  ) => void;
  onUpdate?: (
    df: UpdateDataFieldTemplateBody,
    op: UpdateDataFieldOptionTemplateBody[]
  ) => void;
  dataField?: any;
  toggleEdit?: () => void;
}) {
  const [createDataFieldOptions] = useCreateDataFieldOptionTemplateMutation();
  const [updateOptions] = useUpdateDataFieldOptionsTemplateMutation();
  const [updateDataFieldTemplate] = useUpdateDataFieldTemplateMutation();
  const [deleteDataFieldOptionTemplate] =
    useDeleteDataFieldOptionTemplateMutation();
  const [indexOfCurrentScreen, setIndexOfCurrentScreen] = useState<number>(0);
  const [createDataFieldTemplateBody, setCreateDataFieldTemplateBody] =
    useState({
      name: "",
      description: "",
      type: "",
    });
  const [
    createDataFieldOptionTemplateBody,
    setCreateDataFieldOptionTemplateBody,
  ] = useState<any[]>([]);

  useEffect(() => {
    setCreateDataFieldTemplateBody({
      name: dataField?.name ?? "",
      description: dataField?.description ?? "",
      type: dataField?.type ?? "",
    });

    setCreateDataFieldOptionTemplateBody(dataField?.dataFieldOptions || []);
  }, [dataField]);

  const handleAddDataField = () => {
    if (onAdd) {
      onAdd(
        {
          ...createDataFieldTemplateBody,
          type: createDataFieldTemplateBody.type as DataFieldType,
        },
        createDataFieldOptionTemplateBody
      );
      setCreateDataFieldOptionTemplateBody([]);
    }
  };

  const singleValueDataFields = [
    "input",
    "textarea",
    "number",
    "email",
    "date",
    "dateTime",
    "yesNo",
    "toggle",
    "checkbox",
    "textbox"
  ];

  const handleUpdateDataField = () => {
    // const options = [...createDataFieldOptionTemplateBody];

    const updatedOptions = createDataFieldOptionTemplateBody.map((option, index) => {
      if (selectedCheckboxes.includes(option.value)) {
        const updatedOption = {
          ...option,
          priorityTopics: option.value,
        };
        return updatedOption;
      }
      
      else{
        return {
          ...option,
          priorityTopics: null,
          reviewStatements: null
        }
      }
      
    });
    

    setCreateDataFieldOptionTemplateBody(updatedOptions)
    
    const deleteDataFieldOptions = singleValueDataFields.includes(
      createDataFieldTemplateBody.type
    );

    updateDataFieldTemplate({
      id: dataField.id,
      body: createDataFieldTemplateBody as UpdateDataFieldTemplateBody,
    }).then((res: any) => {
      if (updatedOptions.length > 0 && 'data' in res) {
        for (const option of updatedOptions) {
          if (!deleteDataFieldOptions) {
            if ('id' in option) {
              const { id, ...properties } = option;
              updateOptions({ id: id as number, ...properties });
            } else {
              const newOption = { ...option };
              newOption.dataFieldID = dataField.id;
              createDataFieldOptions(newOption);
            }
          } else {
            deleteDataFieldOptionTemplate(option.id);
          }
        }
      }
      toggleEdit && toggleEdit();
      onClose && onClose();
    });
  };

  const validateOptionsData = (
    isTileSelect: boolean,
    data: CreateDataFieldOptionTemplateBody[]
  ) => {
    if (data.length === 0) return false;
    return data.every((option) =>
      isTileSelect ? !!option.value && !!option.icon : !!option.value
    );
  };

  const isFirstStepEnable =
    createDataFieldTemplateBody.name !== "" &&
    createDataFieldTemplateBody.description !== "";
  const isTileSelectOption =
    createDataFieldTemplateBody.type === "tileSelect" ||
    createDataFieldTemplateBody.type === "tileMultiSelect";
  const hasOption =
    createDataFieldTemplateBody.type === "select" ||
    createDataFieldTemplateBody.type === "multiSelect" ||
    isTileSelectOption;
  const isSecondStepEnable = hasOption
    ? !!createDataFieldTemplateBody.type &&
      validateOptionsData(isTileSelectOption, createDataFieldOptionTemplateBody)
    : !!createDataFieldTemplateBody.type;

  const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>(() => {
    const savedSelectedCheckboxes = localStorage.getItem('selectedCheckboxes');
    return savedSelectedCheckboxes ? JSON.parse(savedSelectedCheckboxes) : [];
  });
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [reviewStatements, setReviewStatements] = useState<string[]>([])
  const [colorValue, setColorValue] = useState<string[]>([]);

  useEffect(() => {
    localStorage.setItem('selectedCheckboxes', JSON.stringify(selectedCheckboxes));
  }, [selectedCheckboxes]);

  const handleCheckboxChange = (optionId: number) => {
    setSelectedCheckboxes((prevState) =>
      prevState.includes(optionId)
        ? prevState.filter((id) => id !== optionId)
        : [...prevState, optionId]
    );
  };

  const handleSelectedValueChange = (index: number, value: string) => {
    const newSelectedValues = [...selectedValues];
    newSelectedValues[index] = value;
    setSelectedValues(newSelectedValues);
  };

  const handleReviewStatementChange = (index: number, value: string) => {
    const newReviewStatements = [...reviewStatements]
    newReviewStatements[index] = value;
    setReviewStatements(newReviewStatements)
  }

  const handleColorsUpdate = (index: number, value: string) => {
    const newColors = [...colorValue];
    newColors[index] = value;
    setColorValue(newColors);
  };

  const isWorkflowDataField = dataField?.name === "Workflow data field";

  return (
    <Box>
      <Box
        sx={{
          diplay: "flex",
          minWidth: "100%",
          maxHeight: isWorkflowDataField ? "calc(80vh - 120px)" : "600px",
          overflow: "auto",
        }}
      >
        {indexOfCurrentScreen === 0 && (
          <FirstStep
            value={createDataFieldTemplateBody as CreateDataFieldTemplateBody}
            onChange={(e: { name: string; desc: string }) => {
              setCreateDataFieldTemplateBody({
                ...createDataFieldTemplateBody,
                name: e.name,
                description: e.desc,
              });
            }}
          />
        )}
        {indexOfCurrentScreen === 1 && (
          <SecondStep
            options={createDataFieldOptionTemplateBody}
            type={createDataFieldTemplateBody.type as DataFieldType}
            onChange={(options, type) => {
              setCreateDataFieldTemplateBody({
                ...createDataFieldTemplateBody,
                type: type,
              });
              setCreateDataFieldOptionTemplateBody(options);
            }}
            dataField={dataField}
            selectedCheckboxes={selectedCheckboxes}
            handleCheckboxChange={handleCheckboxChange}
            onSelectedValueChange={handleSelectedValueChange}
            onReviewStatementChange= {handleReviewStatementChange}
            handleColorChange={(index, value)=> {
              const updatedOptions = createDataFieldOptionTemplateBody.map((option, i)=> {
                if(i === index) {
                  const updatedOption = {...option}
                  updatedOption.colors = value;
                  return updatedOption;
                }
                return option
              });
              setCreateDataFieldOptionTemplateBody(updatedOptions)
            }}
            onUpdateReviewStatements={(index, value)=> {
              const updatedOptions = createDataFieldOptionTemplateBody.map((option, i)=> {
                if(i === index) {
                  const updatedOption = {...option}
                  updatedOption.reviewStatements = value;
                  return updatedOption;
                }
                return option
              });
              setCreateDataFieldOptionTemplateBody(updatedOptions)
            }}
            handleTooltipValueChange = {(index, value)=>{
              const updatedOptionsList = createDataFieldOptionTemplateBody.map((option, i)=> {
                if(i === index) {
                  const updatedOption = {...option}
                  updatedOption.multiselectTooltipValue = value;
                  return updatedOption;
                }
                return option
              });
              setCreateDataFieldOptionTemplateBody(updatedOptionsList)
            }}
            handleSepProgressBarChange= {(index, value)=>{
              const updatedProgessBarValue = createDataFieldOptionTemplateBody.map((option, i)=> {
                if(i === index) {
                  const updatedToggleBar = {...option}
                  updatedToggleBar.sepProgressBar = value;
                  return updatedToggleBar;
                }
                return option
              });
              setCreateDataFieldOptionTemplateBody(updatedProgessBarValue)
            }}
            handleHidePowerBiLinkChange = {(index, value)=>{
              const updatedProgessBarValue = createDataFieldOptionTemplateBody.map((option, i)=> {
                if(i === index) {
                  const updatedToggleBar = {...option}
                  updatedToggleBar.powerbiLink = value;
                  return updatedToggleBar;
                }
                return option
              });
              setCreateDataFieldOptionTemplateBody(updatedProgessBarValue)
            }}
          />
        )}
        <StepperDots steps={2} activeStep={indexOfCurrentScreen} />
      </Box>
      <Divider sx={{ backgroundColor: "#7E8083" }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "right",
          p: 2,
          "& > button": {
            m: 1,
          },
        }}
      >
        {indexOfCurrentScreen !== 0 && (
          <Button
            variant="text"
            disabled={false}
            onClick={() => setIndexOfCurrentScreen(0)}
            sx={{ textTransform: "none" }}
          >
            <Typography
              sx={{
                font: "normal normal 600 16px/22px Open Sans",
                color: "#2372B9",
              }}
            >
              <i className="fa-regular fa-angle-left"></i> &nbsp;Back
            </Typography>
          </Button>
        )}

        <Button
          variant="contained"
          onClick={() => {
            indexOfCurrentScreen === 0
              ? setIndexOfCurrentScreen(1)
              : dataField
              ? handleUpdateDataField()
              : handleAddDataField();
          }}
          disabled={
            indexOfCurrentScreen === 0
              ? !isFirstStepEnable
              : !isSecondStepEnable
          }
          sx={{
            textTransform: "none",
            backgroundColor: "#2372B9",
            font: "normal normal 600 16px/22px Open Sans",
          }}
        >
          {indexOfCurrentScreen === 0
            ? "Continue"
            : (dataField ? "Edit" : "Add") + " Data Field"}
        </Button>
      </Box>
    </Box>
  );
}
